import { useCallback, useState } from 'react';
import {
  READER_DEFAULT_FONT_SCALE_INDEX,
  READER_FONT_SCALE_STEPS,
  READER_THEME_IDS,
  READER_LAYOUT_MODES,
} from './readerThemes.js';
import type { ReaderLayoutMode, ReaderThemeId } from './readerThemes.js';

export interface ReaderPrefs {
  themeId: ReaderThemeId;
  fontScaleIndex: number;
  layoutMode: ReaderLayoutMode;
}

export interface UseReaderPrefsResult extends ReaderPrefs {
  fontScale: number;
  setThemeId: (themeId: ReaderThemeId) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  setLayoutMode: (mode: ReaderLayoutMode) => void;
  canIncreaseFontSize: boolean;
  canDecreaseFontSize: boolean;
}

const STORAGE_KEY = 'xdocs.reader-prefs';

const DEFAULT_PREFS: ReaderPrefs = {
  themeId: 'light',
  fontScaleIndex: READER_DEFAULT_FONT_SCALE_INDEX,
  // `paginated` (one chapter per screen + Previous/Next) is the primary F66
  // reading experience per design/app/30-reader.html; `scroll` is the
  // secondary toggle.
  layoutMode: 'paginated',
};

function isReaderThemeId(value: unknown): value is ReaderThemeId {
  return typeof value === 'string' && (READER_THEME_IDS as readonly string[]).includes(value);
}

function isReaderLayoutMode(value: unknown): value is ReaderLayoutMode {
  return typeof value === 'string' && (READER_LAYOUT_MODES as readonly string[]).includes(value);
}

function loadPrefs(): ReaderPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<ReaderPrefs>;
    return {
      themeId: isReaderThemeId(parsed.themeId) ? parsed.themeId : DEFAULT_PREFS.themeId,
      fontScaleIndex:
        typeof parsed.fontScaleIndex === 'number' && parsed.fontScaleIndex >= 0 && parsed.fontScaleIndex < READER_FONT_SCALE_STEPS.length
          ? parsed.fontScaleIndex
          : DEFAULT_PREFS.fontScaleIndex,
      layoutMode: isReaderLayoutMode(parsed.layoutMode) ? parsed.layoutMode : DEFAULT_PREFS.layoutMode,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: ReaderPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // best-effort — reading still works without persisted prefs.
  }
}

/**
 * Reader-scoped display prefs (theme/font-size/layout mode) — local-only,
 * distinct from `reader.position` (server-synced) and the app/document
 * theme axes (CODE_GUIDELINES §4/§28.3). Persisted so returning to the same
 * browser keeps the reader's chosen presentation.
 */
export function useReaderPrefs(): UseReaderPrefsResult {
  const [prefs, setPrefs] = useState<ReaderPrefs>(loadPrefs);

  const update = useCallback((next: Partial<ReaderPrefs>) => {
    setPrefs((prev) => {
      const merged = { ...prev, ...next };
      savePrefs(merged);
      return merged;
    });
  }, []);

  const setThemeId = useCallback((themeId: ReaderThemeId) => update({ themeId }), [update]);
  const setLayoutMode = useCallback((layoutMode: ReaderLayoutMode) => update({ layoutMode }), [update]);
  const increaseFontSize = useCallback(
    () => update({ fontScaleIndex: Math.min(prefs.fontScaleIndex + 1, READER_FONT_SCALE_STEPS.length - 1) }),
    [prefs.fontScaleIndex, update],
  );
  const decreaseFontSize = useCallback(() => update({ fontScaleIndex: Math.max(prefs.fontScaleIndex - 1, 0) }), [prefs.fontScaleIndex, update]);

  return {
    ...prefs,
    fontScale: READER_FONT_SCALE_STEPS[prefs.fontScaleIndex] ?? 1,
    setThemeId,
    increaseFontSize,
    decreaseFontSize,
    setLayoutMode,
    canIncreaseFontSize: prefs.fontScaleIndex < READER_FONT_SCALE_STEPS.length - 1,
    canDecreaseFontSize: prefs.fontScaleIndex > 0,
  };
}
